import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Paper, Box, Stack, Grid, Title as MantineTitle, Text, Group, NumberInput, Table, Anchor } from '@mantine/core';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Legend } from 'recharts';
import { investmentDecisionData, COLORS, IconMapping, rmbInterestRates } from '../../data';

interface InvestmentAnalysisPanelProps {
    agentRecommendedPrice: number;
    actualMonthlyRent: number;
    taxRate: number;
    // Props for setters if direct editing in this panel is desired in future:
    // setAgentRecommendedPrice: (value: number) => void;
    // setTaxRate: (value: number) => void;
}

const InvestmentAnalysisPanel: React.FC<InvestmentAnalysisPanelProps> = ({
    agentRecommendedPrice,
    actualMonthlyRent,
    taxRate
}) => {
    const marketValuation = 1395000;
    const monthlyRent = actualMonthlyRent;
    const annualRent = monthlyRent * 12;

    // Traditional calculations (based on agent recommended price)
    const traditionalRentalYield = agentRecommendedPrice > 0 ? (annualRent / agentRecommendedPrice) * 100 : 0;
    const traditionalPaybackPeriod = annualRent > 0 ? agentRecommendedPrice / annualRent : Infinity;

    // Calculations considering tax
    const netProceedsAfterTax = agentRecommendedPrice * (1 - taxRate);
    const yieldOnNetProceeds = netProceedsAfterTax > 0 ? (annualRent / netProceedsAfterTax) * 100 : 0;
    const paybackPeriodOnNetProceeds = annualRent > 0 ? netProceedsAfterTax / annualRent : Infinity;

    const formatNumber = (num: number, decimals: number = 2) => {
        if (num === Infinity) return "N/A (無租金)";
        return num.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    };
    
    const formatPercentage = (num: number) => {
        if (num === Infinity || isNaN(num)) return "N/A";
        return num.toFixed(2) + '%';
    }

    // Format date for XAxis
    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('zh-CN', { year: 'numeric', month: 'short' });
    };
    
    // Custom tooltip for the line chart
    const CustomTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            return (
                <Paper p="xs" withBorder shadow="md">
                    <Text size="sm">{`日期: ${formatDate(label)}`}</Text>
                    <Text size="sm" c="blue.6">{`利率: ${payload[0].value.toFixed(2)}%`}</Text>
                    <Text size="xs" c="dimmed">{`來源: ${payload[0].payload.url}`}</Text>
                </Paper>
            );
        }
        return null;
    };

    return (
        <Stack gap="lg">
            <Card>
                <CardHeader><CardTitle><Group gap="xs"><IconMapping.Calculator size={20}/>37.5平方米商舖投資分析</Group></CardTitle></CardHeader>
                <CardContent pt="md">
                    <Grid>
                        <Grid.Col span={{ base: 12, md: 4 }}>
                            <MantineTitle order={4} mb="sm">當前參數 (來自上方概覽面板)</MantineTitle>
                            <Stack gap="xs" mb="md">
                                <Group justify="space-between">
                                    <Text c="dimmed">中介推薦價:</Text>
                                    <Text fw={500}>{formatNumber(agentRecommendedPrice, 0)}元</Text>
                                </Group>
                                <Group justify="space-between">
                                    <Text c="dimmed">實際月租金:</Text>
                                    <Text fw={500}>{formatNumber(actualMonthlyRent, 0)}元</Text>
                                </Group>
                                <Group justify="space-between">
                                    <Text c="dimmed">綜合稅率:</Text>
                                    <Text fw={500}>{formatPercentage(taxRate * 100)}</Text>
                                </Group>
                                <Text size="xs" c="dimmed" mt="sm">
                                    如需調整這些參數，請返回"概覽"面板進行修改。
                                </Text>
                            </Stack>
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, md: 8 }}>
                            <MantineTitle order={4} mb="sm">目前投資狀況</MantineTitle>
                            <Stack gap="xs">
                                <Group justify="space-between"><Text c="dimmed">市場估值</Text><Text fw={500}>{formatNumber(marketValuation,0)}元</Text></Group>
                                <Group justify="space-between"><Text c="dimmed">目前月租金</Text><Text fw={500}>{formatNumber(monthlyRent,0)}元</Text></Group>
                                <Group justify="space-between"><Text c="dimmed">年租金收入</Text><Text fw={500}>{formatNumber(annualRent,0)}元</Text></Group>
                                
                                <MantineTitle order={5} mt="xs" mb="xs">傳統回報率計算 (基於<Text span fw={700} c="blue">{formatNumber(agentRecommendedPrice,0)}元</Text>推薦價)</MantineTitle>
                                <Group justify="space-between"><Text c="dimmed" ml="md">租金回報率</Text><Text fw={500}>{formatPercentage(traditionalRentalYield)}</Text></Group>
                                <Group justify="space-between"><Text c="dimmed" ml="md">回本年期</Text><Text fw={500}>{traditionalPaybackPeriod === Infinity ? 'N/A' : formatNumber(traditionalPaybackPeriod,1) + '年'}</Text></Group>
                                
                                <MantineTitle order={5} mt="xs" mb="xs">考慮出售時稅務影響下的實際回報視角 (稅率 <Text span fw={700} c="red">{formatPercentage(taxRate*100)}</Text>)</MantineTitle>
                                <Group justify="space-between"><Text c="dimmed" ml="md">稅後實際可回收資金</Text><Text fw={500}>{formatNumber(netProceedsAfterTax,0)}元</Text></Group>
                                <Group justify="space-between"><Text c="dimmed" ml="md">基於稅後可回收資金的租金回報率</Text><Text fw={500}>{formatPercentage(yieldOnNetProceeds)}</Text></Group>
                                <Group justify="space-between"><Text c="dimmed" ml="md">基於稅後可回收資金的回本年期</Text><Text fw={500}>{paybackPeriodOnNetProceeds === Infinity ? 'N/A' : formatNumber(paybackPeriodOnNetProceeds,1) + '年'}</Text></Group>
                            </Stack>
                        </Grid.Col>
                    </Grid>
                    <Grid mt="md">
                        <Grid.Col span={{ base: 12, md: 6 }}>
                            <Box mt="lg">
                                <MantineTitle order={4} mb="sm">投資回報分析</MantineTitle>
                                <Text>從傳統的租金回報率來看（基於<Text span fw={700}>{formatNumber(agentRecommendedPrice,0)}元</Text>的推薦價），該商舖 <Text span fw={700}>{formatPercentage(traditionalRentalYield)}</Text> 的回報率，相對於其 <Text span fw={700}>{traditionalPaybackPeriod === Infinity ? 'N/A' : formatNumber(traditionalPaybackPeriod,1)} 年</Text> 的回本年期，可能吸引力不足（尤其若低於銀行存款利率）。</Text>
                                <Text mt="sm">然而，若考慮到出售時需支付 <Text span fw={700} c="red">{formatPercentage(taxRate*100)}</Text> 的稅款，實際能拿回的資金約為 <Text span fw={700}>{formatNumber(netProceedsAfterTax,0)}元</Text>。基於此金額，目前的年租金收入（<Text span fw={700}>{formatNumber(annualRent,0)}元</Text>）相當於 <Text span fw={700}>{formatPercentage(yieldOnNetProceeds)}</Text> 的回報率。這個「實際機會成本回報率」可能更具參考價值。</Text>
                                <Text mt="sm">此分析需結合未來市場預期：1. 資產價值變動風險：商舖價格若持續承壓，持有期間資產本身可能貶值。2. 流動性與稅負影響：高額稅費實質上降低了資產的淨值和流動性。</Text>
                            </Box>
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, md: 6 }}>
                            <Box mt="lg"> {/* Adjusted mt for alignment */}
                                <MantineTitle order={4} mb="sm">投資建議</MantineTitle>
                                <Text>在高達 <Text span fw={700} c="red">{formatPercentage(taxRate*100)}</Text> 的出售稅負下，決策核心在於比較「繼續持有，獲取相對於 <Text span fw={700}>{formatNumber(netProceedsAfterTax,0)}元</Text> 稅後可回收資金的 <Text span fw={700}>{formatPercentage(yieldOnNetProceeds)}</Text> 機會成本回報，但承擔資產貶值風險」與「立即出售，承受稅損，將稅後資金另作他用」。</Text>
                                <Text mt="sm">1. 關於出售：以 <Text span fw={700}>{formatNumber(agentRecommendedPrice,0)}元</Text> 售出，稅後實際到手約 <Text span fw={700}>{formatNumber(netProceedsAfterTax,0)}元</Text>。業主需評估此金額是否有更優投資渠道。若市場持續下行，早售雖有稅損，亦可避免更大貶值。然而，稅負本身構成巨大「退出成本」。</Text>
                                <Text mt="sm">2. 關於繼續持有與出租：若短期無法以滿意價格（扣除稅費後）出售，或無更優再投資機會，則繼續持有以獲取 <Text span fw={700}>{formatPercentage(yieldOnNetProceeds)}</Text> 的租金回報（相對於稅後可回收資金），不失為一個避免立即稅務損失的策略。此時應積極管理租賃，爭取更高租金。並密切關注市場與政策。</Text>
                                <Text mt="sm">總結：高額稅費使決策複雜化。傳統 <Text span fw={700}>{formatPercentage(traditionalRentalYield)}</Text> 回報率可能提示出售，但 <Text span fw={700} c="red">{formatPercentage(taxRate*100)}</Text> 的稅收使繼續持有（以 <Text span fw={700}>{formatPercentage(yieldOnNetProceeds)}</Text> 回報於稅後可回收資金）成為需審慎評估的選項。決策應基於市場判斷、風險承受力及資金配置方案。</Text>
                            </Box>
                        </Grid.Col>
                    </Grid>
                </CardContent>
            </Card>
             <Grid>
                <Grid.Col span={{ base: 12, md: 6 }}><Card>
                    <CardHeader><CardTitle><Group gap="xs"><IconMapping.Landmark size={20}/>出售方案分析</Group></CardTitle></CardHeader>
                    <CardContent pt="md">
                        <MantineTitle order={4} mb="sm">出售優勢</MantineTitle>
                        <Stack gap="xs">
                            <Group gap="xs" align="flex-start"><IconMapping.CheckCircle2 size={20} color="green"/><Text>規避未來市場下行風險，鎖定目前資產價值（稅前 <Text span fw={500}>{formatNumber(agentRecommendedPrice,0)}元</Text>）</Text></Group>
                            <Group gap="xs" align="flex-start"><IconMapping.CheckCircle2 size={20} color="green"/><Text>釋放資金（稅後約 <Text span fw={500}>{formatNumber(netProceedsAfterTax,0)}元</Text>），可投資於其他回報率更高的項目</Text></Group>
                            <Group gap="xs" align="flex-start"><IconMapping.CheckCircle2 size={20} color="green"/><Text>避免未來可能的租戶問題和物業管理壓力</Text></Group>
                            <Group gap="xs" align="flex-start"><IconMapping.CheckCircle2 size={20} color="green"/><Text>中介推薦價 <Text span fw={500}>{formatNumber(agentRecommendedPrice,0)}元</Text> 雖低於市場估值，但在目前市場環境下或屬合理</Text></Group>
                        </Stack>
                        <MantineTitle order={4} mt="lg" mb="sm">出售劣勢</MantineTitle>
                        <Stack gap="xs">
                            <Group gap="xs" align="flex-start"><IconMapping.XCircle size={20} color="red"/><Text>高達 <Text span fw={700} c="red">{formatPercentage(taxRate*100)}</Text> 的巨額稅費導致實際到手金額遠低於市場售價</Text></Group>
                            <Group gap="xs" align="flex-start"><IconMapping.XCircle size={20} color="red"/><Text>失去穩定的租金收入來源（年 <Text span fw={500}>{formatNumber(annualRent,0)}元</Text>）</Text></Group>
                            <Group gap="xs" align="flex-start"><IconMapping.XCircle size={20} color="red"/><Text>如果未來區域商業環境改善，可能錯失資產增值機會</Text></Group>
                            <Group gap="xs" align="flex-start"><IconMapping.XCircle size={20} color="red"/><Text>目前市場交易不活躍，可能需要較長時間才能找到合適買家</Text></Group>
                            <Group gap="xs" align="flex-start"><IconMapping.XCircle size={20} color="red"/><Text>交易過程中仍需支付中介費等額外成本</Text></Group>
                        </Stack>
                         <MantineTitle order={4} mt="lg" mb="sm">出售建議</MantineTitle>
                         <Text>如選擇出售，務必清晰計算稅後淨得。建議以 <Text span fw={500}>{formatNumber(agentRecommendedPrice,0)}元</Text> 至 <Text span fw={500}>{formatNumber(marketValuation,0)}元</Text> 作為掛牌價格區間，根據市場反饋適當調整。可以考慮多家中介同時代理，以提高曝光率。同時，可以適當裝修改善商舖狀況，提高吸引力。出售過程中可繼續出租，保持現金流，但需明確告知潛在買家租賃情況。</Text>
                    </CardContent>
                </Card></Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}><Card>
                    <CardHeader><CardTitle><Group gap="xs"><IconMapping.Wallet size={20}/>繼續出租方案分析</Group></CardTitle></CardHeader>
                    <CardContent pt="md">
                         <MantineTitle order={4} mb="sm">出租優勢</MantineTitle>
                        <Stack gap="xs">
                            <Group gap="xs" align="flex-start"><IconMapping.CheckCircle2 size={20} color="green"/><Text>保持穩定的租金收入來源（年 <Text span fw={500}>{formatNumber(annualRent,0)}元</Text>）</Text></Group>
                            <Group gap="xs" align="flex-start"><IconMapping.CheckCircle2 size={20} color="green"/><Text>保留資產，等待未來可能的市場回暖</Text></Group>
                            <Group gap="xs" align="flex-start"><IconMapping.CheckCircle2 size={20} color="green"/><Text>避免在市場低迷時期以 <Text span fw={500}>{formatNumber(agentRecommendedPrice,0)}元</Text> (稅後 <Text span fw={500}>{formatNumber(netProceedsAfterTax,0)}元</Text>) 出售，可能未來獲得更好價格</Text></Group>
                            <Group gap="xs" align="flex-start"><IconMapping.CheckCircle2 size={20} color="green"/><Text>目前租金 <Text span fw={500}>{formatNumber(monthlyRent,0)}元/月</Text> 處於市場合理水平，租戶相對穩定</Text></Group>
                        </Stack>
                        <MantineTitle order={4} mt="lg" mb="sm">出租劣勢</MantineTitle>
                        <Stack gap="xs">
                            <Group gap="xs" align="flex-start">
                                <IconMapping.XCircle size={20} color="red"/>
                                <Text>傳統租金回報率 <Text span fw={700}>{formatPercentage(traditionalRentalYield)}</Text>（基於全額售價），可能低於銀行存款利率。相對於稅後可回收資金的機會成本回報率為 <Text span fw={700}>{formatPercentage(yieldOnNetProceeds)}</Text>。</Text>
                            </Group>
                            <Group gap="xs" align="flex-start"><IconMapping.XCircle size={20} color="red"/><Text>面臨租戶流失、空置期等風險</Text></Group>
                            <Group gap="xs" align="flex-start"><IconMapping.XCircle size={20} color="red"/><Text>需要持續管理和維護商舖，增加管理成本</Text></Group>
                            <Group gap="xs" align="flex-start"><IconMapping.XCircle size={20} color="red"/><Text>未來商舖價格可能繼續下跌，資產價值面臨貶值風險</Text></Group>
                        </Stack>
                         <MantineTitle order={4} mt="lg" mb="sm">出租建議</MantineTitle>
                         <Text>如選擇繼續出租，建議在租約到期後適當提高租金至2,300-2,500元/月，以提升回報率。同時，可以考慮尋找更優質、穩定的租戶。此外，可以適當投入改善商舖條件，提高租金水平和出租率。</Text>
                    </CardContent>
                </Card></Grid.Col>
            </Grid>
            <Card>
                <CardHeader><CardTitle><Group gap="xs"><IconMapping.HelpCircle size={20}/>中介推薦價格與租金水平驗證</Group></CardTitle></CardHeader>
                <CardContent pt="md">
                    <Grid>
                        <Grid.Col span={{ base: 12, md: 6 }}>
                            <MantineTitle order={4} mb="sm">中介推薦價格分析 (可調推薦價: <Text span fw={700} c="blue">{formatNumber(agentRecommendedPrice,0)}元</Text>)</MantineTitle>
                            <Text mb="sm">中介推薦的售價為 <Text span fw={500}>{formatNumber(agentRecommendedPrice,0)}元</Text>，約合 <Text span fw={500}>{formatNumber(agentRecommendedPrice / 37.5,0)}元/平方米</Text>。此價格相較於市場估值 <Text span fw={500}>{formatNumber(marketValuation,0)}元</Text>（<Text span fw={500}>{formatNumber(marketValuation / 37.5,0)}元/平方米</Text>）的折價率約為 <Text span fw={500}>{formatPercentage(((marketValuation - agentRecommendedPrice) / marketValuation) * 100)}</Text>。稅前價格在目前市場交易不活躍及未來下行壓力下是否合理，業主需結合稅後淨收益（約 <Text span fw={700} c="green">{formatNumber(netProceedsAfterTax,0)}元</Text>，按 <Text span fw={700} c="red">{formatPercentage(taxRate*100)}</Text> 稅率計）進行判斷。</Text>
                            <Text mb="sm">芳村區域小區內商舖的均價參考為35,000元/平方米。考慮到該商舖位於小區內、非臨街、非地鐵口因素，其商業價值相對較低，推薦價需反映此點。</Text>
                            <Text>綜合考慮，中介推薦的 <Text span fw={500}>{formatNumber(agentRecommendedPrice,0)}元</Text> 售價（稅前）可作為談判起點，但最終決策應基於詳細的稅後淨收益分析和個人財務目標。</Text>
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, md: 6 }}>
                            <MantineTitle order={4} mb="sm">目前租金水平驗證</MantineTitle>
                            <Text mb="sm">目前月租金為 <Text span fw={500}>{formatNumber(monthlyRent,0)}元</Text>，約合 <Text span fw={500}>{formatNumber(monthlyRent / 37.5, 0)}元/平方米/月</Text>。芳村區域小區內商舖的月租金水平參考為50-60元/平方米/月，目前租金處於此範圍內。</Text>
                            <Text mb="sm">考慮到商舖位置因素，目前租金水平反映其商業價值。從傳統租金回報率（<Text span fw={700}>{formatPercentage(traditionalRentalYield)}</Text>，基於 <Text span fw={700} c="blue">{formatNumber(agentRecommendedPrice,0)}元</Text> 推薦價）來看，投資價值可能偏低。然而，若從避免 <Text span fw={700} c="red">{formatPercentage(taxRate*100)}</Text> 即時稅損、著眼於稅後可回收資金（<Text span fw={700}>{formatNumber(netProceedsAfterTax,0)}元</Text>）的機會成本回報（<Text span fw={700}>{formatPercentage(yieldOnNetProceeds)}</Text>）角度，繼續持有並出租亦有其合理性。</Text>
                            <Text>綜合考慮，目前月租金 <Text span fw={500}>{formatNumber(monthlyRent,0)}元</Text> 屬市場合理水平。若繼續出租，可考慮在租約到期後適當提租，以提升相對於稅後可回收資金的回報率。</Text>
                        </Grid.Col>
                    </Grid>
                </CardContent>
            </Card>
            <Card>
                <CardHeader>
                    <CardTitle>
                        <Group gap="xs">
                            <IconMapping.Banknote size={20} />
                            人民幣一年期存款利率走勢 (中國銀行)
                        </Group>
                    </CardTitle>
                </CardHeader>
                <CardContent pt="md">
                    <Box style={{ height: 400 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart
                                data={rmbInterestRates.slice().sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())}
                                margin={{
                                    top: 5,
                                    right: 30,
                                    left: 0,
                                    bottom: 5,
                                }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis 
                                    dataKey="date" 
                                    tickFormatter={formatDate} 
                                    angle={-30} 
                                    textAnchor="end"
                                    height={60}
                                    interval="preserveStartEnd"
                                />
                                <YAxis 
                                    dataKey="rate" 
                                    tickFormatter={(tick) => `${tick.toFixed(2)}%`}
                                    domain={['dataMin - 0.25', 'dataMax + 0.25']}
                                />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend formatter={(value) => "一年期利率"}/>
                                <Line type="monotone" dataKey="rate" stroke={COLORS[0]} activeDot={{ r: 8 }} name="一年期利率" />
                            </LineChart>
                        </ResponsiveContainer>
                    </Box>
                    <Text size="xs" c="dimmed" mt="sm">
                        數據點擊可跳轉至中國銀行官方公告頁面。圖表展示了歷史上中國銀行發布的一年期人民幣整存整取存款基準利率變化。
                    </Text>

                    <MantineTitle order={5} mt="lg" mb="sm">利率數據表</MantineTitle>
                    <Box style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        <Table striped highlightOnHover withTableBorder withColumnBorders>
                            <Table.Thead style={{ position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
                                <Table.Tr>
                                    <Table.Th>生效日期</Table.Th>
                                    <Table.Th ta="right">利率</Table.Th>
                                    <Table.Th>數據來源 (中國銀行官網)</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {rmbInterestRates
                                    .slice()
                                    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()) // Sort by date, newest first
                                    .map((item) => (
                                        <Table.Tr key={item.date}>
                                            <Table.Td>{item.date}</Table.Td>
                                            <Table.Td ta="right">{item.rate.toFixed(2)}%</Table.Td>
                                            <Table.Td>
                                                <Anchor href={item.url} target="_blank" size="xs">
                                                    {item.url.substring(item.url.lastIndexOf('/') + 1) || item.url}
                                                </Anchor>
                                            </Table.Td>
                                        </Table.Tr>
                                    ))}
                            </Table.Tbody>
                        </Table>
                    </Box>
                </CardContent>
            </Card>
        </Stack>
    );
}

export default InvestmentAnalysisPanel; 